import {
    ConfirmationNumber,
    FolderOpenOutlined,
    InfoOutlined,
    OpenInNew,
    Visibility,
} from '@mui/icons-material';
import {
    Badge,
    Button,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';

import DocumentActionsMenu from './documentActionsMenu';
import DocumentStatusChip from './documentStatusChip';
import DocumentStatusFilter from './documentStatusFilter';
import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { AppDocument } from 'api';
import { DocStatus } from 'data/constants/DocStatus';
import useTranslate from 'hooks/useTranslate';

interface DocumentTableViewProps {
    documents: AppDocument[];
    loading?: boolean;
    uploadingDocId: number | null;
    statusFilter: string | null;
    onStatusFilterChange: (status: string | null) => void;
    onView: (doc: AppDocument) => void;
    onUpload: (doc: AppDocument, file: File) => void;
    onDownload: (doc: AppDocument) => void;
    onDelete: (doc: AppDocument) => void;
    onChangeStatus: (doc: AppDocument, status: DocStatus) => void;
    onPreviewDocumentFile?: (doc: AppDocument, previewType: 'sample' | 'template') => void;
    getExplanationUrl?: (doc: AppDocument) => string | null;
    openTicketCounts?: Record<number, number>;
    onViewTickets?: (doc: AppDocument) => void;
    onCreateTicket?: (doc: AppDocument) => void;
    onChat?: (doc: AppDocument) => void;
    onShowDescription?: (doc: AppDocument) => void;
}

const headCellSx = {
    py: 1.25,
    px: 1.5,
    fontWeight: 700,
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
    py: 1.5,
    px: 1.5,
    verticalAlign: 'middle' as const,
};

const EmptyCellMark = () => (
    <Typography variant="body2" sx={{ color: 'grey.300', fontWeight: 400, userSelect: 'none' }}>
        —
    </Typography>
);

const DocumentTableView = ({
    documents,
    loading,
    uploadingDocId,
    statusFilter,
    onStatusFilterChange,
    onView,
    onUpload,
    onDownload,
    onDelete,
    onChangeStatus,
    onPreviewDocumentFile,
    getExplanationUrl,
    openTicketCounts = {},
    onViewTickets,
    onCreateTicket,
    onChat,
    onShowDescription,
}: DocumentTableViewProps) => {
    const { translate } = useTranslate();

    if (loading) return <TableSkeleton tableColumns={6} tableRowCount={6} />;

    return (
        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 520, overflow: 'auto' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCellSx}>{translate('document')}</TableCell>
                            <TableCell sx={headCellSx}>
                                <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
                                    {translate('status')}
                                    <DocumentStatusFilter
                                        value={statusFilter}
                                        onChange={onStatusFilterChange}
                                    />
                                </Stack>
                            </TableCell>
                            <TableCell sx={headCellSx}>{translate('tickets')}</TableCell>
                            <TableCell sx={headCellSx} align="center">
                                {translate('documentsSample')}
                            </TableCell>
                            <TableCell sx={headCellSx} align="center">
                                {translate('documentsTemplate')}
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, pr: 3 }} align="right">
                                {translate('actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!documents.length ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ border: 0, py: 4 }}>
                                    <Result
                                        icon={<FolderOpenOutlined sx={{ fontSize: 32 }} />}
                                        title={translate('noMatchingDocuments')}
                                        description={translate('noMatchingDocumentsDescription')}
                                    />
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {documents.map((doc) => {
                            const ticketCount = openTicketCounts[doc.id] ?? 0;
                            const hasFile = Boolean(doc.resource_name);
                            const explanationUrl = doc.template_uri
                                ? null
                                : (getExplanationUrl?.(doc) ?? null);
                            const isExternal = Boolean(explanationUrl);

                            return (
                                <TableRow key={doc.id} hover>
                                    <TableCell
                                        sx={{
                                            ...bodyCellSx,
                                            maxWidth: 320,
                                            cursor: hasFile ? 'pointer' : 'default',
                                        }}
                                        onClick={() => {
                                            if (hasFile) onView(doc);
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            sx={{ alignItems: 'center' }}
                                        >
                                            <Typography
                                                variant="body2"
                                                noWrap
                                                sx={{
                                                    fontWeight: hasFile ? 600 : 400,
                                                    color: hasFile
                                                        ? 'primary.main'
                                                        : 'text.secondary',
                                                }}
                                            >
                                                {doc.document_name}
                                            </Typography>
                                            <Tooltip title={translate('description')}>
                                                <IconButton
                                                    size="small"
                                                    sx={{ flexShrink: 0 }}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        onShowDescription?.(doc);
                                                    }}
                                                >
                                                    <InfoOutlined
                                                        sx={{
                                                            fontSize: 16,
                                                            color: 'text.disabled',
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <DocumentStatusChip
                                            status={doc.status}
                                            onChangeStatus={(newStatus) =>
                                                onChangeStatus(doc, newStatus)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        {ticketCount > 0 ? (
                                            <Badge badgeContent={ticketCount} color="warning">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    startIcon={
                                                        <ConfirmationNumber
                                                            sx={{
                                                                fontSize: 14,
                                                                color: 'warning.main',
                                                            }}
                                                        />
                                                    }
                                                    onClick={() => onViewTickets?.(doc)}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {translate('viewTickets')}
                                                </Button>
                                            </Badge>
                                        ) : (
                                            <EmptyCellMark />
                                        )}
                                    </TableCell>
                                    <TableCell sx={bodyCellSx} align="center">
                                        {doc.sample_uri ? (
                                            <Tooltip title={translate('previewSample')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        onPreviewDocumentFile?.(doc, 'sample')
                                                    }
                                                >
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <EmptyCellMark />
                                        )}
                                    </TableCell>
                                    <TableCell sx={bodyCellSx} align="center">
                                        {!doc.template_uri && !explanationUrl ? (
                                            <EmptyCellMark />
                                        ) : (
                                            <Tooltip
                                                title={translate(
                                                    isExternal
                                                        ? 'openTemplateLink'
                                                        : 'previewTemplate'
                                                )}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        onPreviewDocumentFile?.(doc, 'template')
                                                    }
                                                >
                                                    {isExternal ? (
                                                        <OpenInNew fontSize="small" />
                                                    ) : (
                                                        <Visibility fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, pr: 3 }} align="right">
                                        <DocumentActionsMenu
                                            hasFile={hasFile}
                                            uploading={uploadingDocId === doc.id}
                                            onCreateTicket={() => onCreateTicket?.(doc)}
                                            onChat={() => onChat?.(doc)}
                                            onUpload={(file) => onUpload(doc, file)}
                                            onDownload={() => onDownload(doc)}
                                            onDelete={() => onDelete(doc)}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default DocumentTableView;
